// ============================================================
// [2.13.0] Console du serveur de comptes — « Serveurs Siverix »
// Les serveurs Siverix connus par les comptes (comptes reliés, favoris),
// leur état à la demande, l'accès à leur console, et le blocage : un serveur
// Siverix bloqué ne peut plus ouvrir de session avec un compte Siverix.
// ============================================================

import { Fragment, useCallback, useEffect, useState } from 'react';
import type { SiverixServerStatus, SiverixServerSummary } from '@siverix/api-client';
import { consoleUrlOf } from '@siverix/shared';
import { api } from '../api';
import { ErrorBox } from './OverviewPage';
import { Badge } from './UsersPage';

type StatusCell =
  | { state: 'loading' }
  | { state: 'done'; result: SiverixServerStatus }
  | { state: 'error'; message: string };

interface Pending {
  url: string;
  mode: 'block' | 'unblock';
}

export function SiverixServersPage(): JSX.Element {
  const [servers, setServers] = useState<SiverixServerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<Record<string, StatusCell>>({});
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const fetchList = useCallback(async (): Promise<void> => {
    setErr(null);
    try {
      const r = await api.adminListSiverixServers();
      setServers(r.servers);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  async function check(url: string): Promise<void> {
    setStatuses((s) => ({ ...s, [url]: { state: 'loading' } }));
    try {
      const result = await api.adminSiverixServerStatus(url);
      setStatuses((s) => ({ ...s, [url]: { state: 'done', result } }));
    } catch (e) {
      setStatuses((s) => ({ ...s, [url]: { state: 'error', message: (e as Error).message } }));
    }
  }

  function ask(url: string, mode: Pending['mode']): void {
    setPending({ url, mode });
    setReason('');
    setActionErr(null);
  }

  async function confirm(): Promise<void> {
    if (!pending) return;
    setBusy(true);
    setActionErr(null);
    try {
      if (pending.mode === 'block') {
        await api.adminBlockSiverixServer(pending.url, reason.trim() || undefined);
      } else {
        await api.adminUnblockSiverixServer(pending.url);
      }
      setPending(null);
      await fetchList();
    } catch (e) {
      setActionErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Serveurs Siverix</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '14px', maxWidth: 760 }}>
        Les serveurs Siverix auxquels des comptes Siverix se sont reliés ou qu'ils ont en favori. Un
        serveur Siverix bloqué ne peut plus ouvrir de session avec un compte Siverix (ni dans
        l'application, ni dans sa console). Chaque blocage est inscrit au journal d'audit.
      </p>

      {err && <ErrorBox message={err} />}

      {loading ? (
        <div>Chargement…</div>
      ) : (
        !err && (
          <div
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              overflowX: 'auto',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ textAlign: 'left', background: 'var(--color-bg)' }}>
                  <th style={th}>Serveur Siverix</th>
                  <th style={th}>Adresse</th>
                  <th style={{ ...th, textAlign: 'right' }}>Comptes reliés</th>
                  <th style={{ ...th, textAlign: 'right' }}>Favoris</th>
                  <th style={th}>Dernier passage</th>
                  <th style={th}>État</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {servers.map((s) => {
                  const status = statuses[s.url];
                  const probedName =
                    status?.state === 'done' && status.result.name ? status.result.name : null;
                  const isOpen = pending?.url === s.url;
                  return (
                    <Fragment key={s.url}>
                      <tr style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={td}>
                          <strong>{s.label ?? probedName ?? hostOf(s.url)}</strong>
                          {probedName && s.label && probedName !== s.label && (
                            <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                              annoncé : {probedName}
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, wordBreak: 'break-all' }}>
                          <code>{s.url}</code>
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>{s.linkedAccounts}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{s.bookmarks}</td>
                        <td style={td}>
                          {s.lastSeenAt ? new Date(s.lastSeenAt).toLocaleString('fr-FR') : '—'}
                        </td>
                        <td style={td}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {s.blocked && <Badge label="BLOQUÉ" tone="error" />}
                            <StatusBadge cell={status} />
                          </div>
                          {s.blocked && s.blockedReason && (
                            <div
                              style={{
                                fontSize: 11,
                                color: 'var(--color-text-muted)',
                                marginTop: 4,
                              }}
                            >
                              Motif : {s.blockedReason}
                            </div>
                          )}
                          {status?.state === 'error' && (
                            <div
                              style={{ fontSize: 11, color: 'var(--color-error)', marginTop: 4 }}
                            >
                              {status.message}
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button
                            type="button"
                            onClick={() => void check(s.url)}
                            disabled={status?.state === 'loading'}
                            style={smallBtnStyle}
                          >
                            {status?.state === 'loading' ? '…' : 'Vérifier'}
                          </button>{' '}
                          {/^https?:\/\//i.test(s.url) && (
                            <a
                              href={consoleUrlOf(s.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ ...smallBtnStyle, display: 'inline-block' }}
                            >
                              Ouvrir sa console
                            </a>
                          )}{' '}
                          <button
                            type="button"
                            onClick={() => ask(s.url, s.blocked ? 'unblock' : 'block')}
                            disabled={isOpen}
                            style={{
                              ...smallBtnStyle,
                              color: s.blocked ? 'var(--color-text)' : 'var(--color-error)',
                            }}
                          >
                            {s.blocked ? 'Débloquer' : 'Bloquer'}
                          </button>
                        </td>
                      </tr>
                      {isOpen && pending && (
                        <tr>
                          <td colSpan={7} style={{ ...td, background: 'var(--color-bg)' }}>
                            <ConfirmPanel
                              pending={pending}
                              reason={reason}
                              onReason={setReason}
                              busy={busy}
                              error={actionErr}
                              onCancel={() => setPending(null)}
                              onConfirm={() => void confirm()}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {servers.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        ...td,
                        color: 'var(--color-text-muted)',
                        textAlign: 'center',
                        padding: '24px',
                      }}
                    >
                      Aucun serveur Siverix pour l'instant.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function ConfirmPanel({
  pending,
  reason,
  onReason,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  pending: Pending;
  reason: string;
  onReason: (v: string) => void;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const block = pending.mode === 'block';
  return (
    <div role="group" aria-label={block ? 'Confirmer le blocage' : 'Confirmer le déblocage'}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {block ? 'Bloquer' : 'Débloquer'} <code>{pending.url}</code> ?
      </div>
      <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--color-text-muted)' }}>
        {block
          ? "Les comptes Siverix ne pourront plus s'y connecter. Les sessions déjà ouvertes sur ce serveur Siverix ne sont pas fermées par le blocage."
          : "Les comptes Siverix pourront de nouveau s'y connecter."}
      </p>
      {block && (
        <label style={{ display: 'block', fontSize: 12, marginBottom: 10 }}>
          Motif (facultatif, visible des administrateurs)
          <input
            type="text"
            value={reason}
            maxLength={500}
            onChange={(e) => onReason(e.target.value)}
            style={inputStyle}
          />
        </label>
      )}
      {error && <ErrorBox message={error} />}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="button" onClick={onCancel} disabled={busy} style={smallBtnStyle}>
          Annuler
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          style={{
            ...smallBtnStyle,
            background: block ? 'var(--color-error)' : 'var(--color-accent)',
            color: '#fff',
            border: 'none',
          }}
        >
          {busy ? '…' : block ? 'Confirmer le blocage' : 'Confirmer le déblocage'}
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ cell }: { cell: StatusCell | undefined }): JSX.Element | null {
  if (!cell || cell.state === 'loading') return null;
  if (cell.state === 'error') return <Badge label="NON VÉRIFIÉ" tone="muted" />;
  const r = cell.result;
  if (!r.reachable) return <Badge label="INJOIGNABLE" tone="error" />;
  const bits = ['EN LIGNE'];
  if (r.version) bits.push(`v${r.version.replace(/^v/i, '')}`);
  if (r.claimed === false) bits.push('NON RÉCLAMÉ');
  return (
    <span title={r.motd ?? undefined}>
      <Badge label={bits.join(' · ')} tone="success" />
    </span>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

const th: React.CSSProperties = {
  padding: '10px 16px',
  fontWeight: 600,
  fontSize: '12px',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
};
const td: React.CSSProperties = { padding: '10px 16px', verticalAlign: 'top' };
const smallBtnStyle: React.CSSProperties = {
  padding: '4px 10px',
  background: 'transparent',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  fontSize: '12px',
  cursor: 'pointer',
  textDecoration: 'none',
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  marginTop: '4px',
  padding: '8px 10px',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  fontSize: '13px',
  fontFamily: 'inherit',
};
